import { isNil } from "lodash-es";
import type { FC } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
  useCreateAssetCallback,
  useUpdateAssetCallback,
} from "../../modules/assets/hooks/use-upsert-asset";
import type { Asset } from "../../modules/assets/server/digital-asset-service";
import type { TemplateCustomizationObject } from "../../modules/design-huddle/brand-info-customizations/types";
import type { UUID } from "../../utils/uuid";
import { ErrorModal } from "../../modules/design-huddle/error-modal";
import { useCreateDesignHuddleProject } from "../../modules/design-huddle/use-create-design-huddle-content-project";
import { useDesignHuddle } from "../../modules/design-huddle/use-design-huddle";
import { EditContent } from "../../modules/edit-content/edit-content";
import { LoadingModal } from "../../modules/edit-content/loading-modal";
import type { SaveAssetHandler } from "../../modules/edit-content/types";
import { Page } from "../../modules/layout/components/page";
import { useContentTypeMultipage } from "../../modules/multiformat/hooks/use-content-type-multipage";
import { fromBase64 } from "../../utils/base64";
import { useDialog } from "../../utils/hooks/use-dialog";

type LocationState = {
  contentType: string;
  customizations: TemplateCustomizationObject;
  page: number;
  templateCode: string;
  projectId?: string;
  customTemplate?: boolean;
  contentGenerationResultId?: UUID;
};

const CreateContentEditPage: FC = () => {
  const navigate = useNavigate();
  const errorDialog = useDialog();
  const [searchParams] = useSearchParams();
  const title = searchParams.get("title") ?? "";
  const projectIdOverride = searchParams.get("projectId") ?? "";

  const [projectId, setProjectId] = useState<string>(projectIdOverride);
  const {
    customizations,
    templateCode,
    page,
    contentType,
    customTemplate,
    contentGenerationResultId,
  } = useMemo(() => {
    const d = searchParams.get("d");
    const decoded = fromBase64(d || "");
    return JSON.parse(decoded) as LocationState;
  }, [searchParams]);

  const { loading: designHuddleLoading, error: designHuddleError } = useDesignHuddle([]);

  const createDesignHuddleProject = useCreateDesignHuddleProject();
  const { data: multipage, loading: multipageLoading } = useContentTypeMultipage(contentType);

  useEffect(() => {
    if (designHuddleError && !errorDialog.open) {
      errorDialog.handleOpen();
      return;
    }
    if (
      designHuddleLoading ||
      isNil(templateCode) ||
      isNil(customizations) ||
      isNil(page) ||
      errorDialog.open ||
      projectId
    )
      return;

    void createDesignHuddleProject({
      templateCode,
      customizations,
      pageNumber: multipage ? undefined : page,
    })
      .then(setProjectId)
      .catch((e) => {
        console.error("Error creating Design Huddle project:", e);
        errorDialog.handleOpen();
      });
  }, [
    createDesignHuddleProject,
    designHuddleLoading,
    designHuddleError,
    errorDialog,
    customizations,
    page,
    projectId,
    templateCode,
    contentType,
    multipage,
  ]);

  const [ready, setReady] = useState(false);
  const handleReady = useCallback(() => setReady(true), []);

  const [asset, setAsset] = useState<Asset | null>(null);

  const createAsset = useCreateAssetCallback();
  const updateAsset = useUpdateAssetCallback();

  const handleSaveAsset: SaveAssetHandler = useCallback(
    async (opts) => {
      const result = asset
        ? await updateAsset({
            asset,
            ...opts,
          })
        : await createAsset({
            projectId,
            contentType,
            customTemplate,
            ...opts,
          });

      result.success && setAsset(result.asset);
      return result;
    },
    [asset, contentType, createAsset, projectId, updateAsset, customTemplate],
  );

  const handleCloseErrorDialog = useCallback(() => {
    if (window.history.length > 1) {
      void navigate(-1);
    } else {
      void navigate("/");
    }
    errorDialog.handleClose();
  }, [errorDialog, navigate]);

  if (errorDialog.open) {
    return <ErrorModal open={errorDialog.open} handleClose={handleCloseErrorDialog} />;
  }

  return (
    <Page title="Edit Content | ChangeEngine" sx={{ height: "100%" }}>
      {projectId && !multipageLoading && (
        <EditContent
          projectId={projectId}
          page={page}
          onReady={handleReady}
          title={title}
          onSave={handleSaveAsset}
          multipage={multipage ?? false}
          contentType={contentType}
          assetId={asset?.id}
          contentGenerationResultId={contentGenerationResultId}
        />
      )}
      {projectId !== "dummy" && <LoadingModal open={!ready} />}
    </Page>
  );
};

export default CreateContentEditPage;
